import React from "react";

// Tracks the on-screen bottom action bars so the rest of the app can react to
// their presence — reserve scroll space for them (see <ActionBarSpacer />) and
// lift overlays above them. A bar registers its measured height on mount and
// unregisters on unmount; the offset is the tallest currently registered.

type ActionBarOffsetContextValue = {
  /** Height (px) of the tallest registered bottom action bar; 0 when none. */
  offset: number;
  register: (id: string, height: number) => void;
  unregister: (id: string) => void;
};

const ActionBarOffsetContext = React.createContext<ActionBarOffsetContextValue>(
  {
    offset: 0,
    register: () => {},
    unregister: () => {},
  },
);

export const ActionBarOffsetProvider: FCwC = ({ children }) => {
  const [barHeights, setBarHeights] = React.useState<Record<string, number>>(
    {},
  );

  const register = React.useCallback((id: string, height: number) => {
    setBarHeights((prev) =>
      prev[id] === height ? prev : { ...prev, [id]: height },
    );
  }, []);

  const unregister = React.useCallback((id: string) => {
    setBarHeights((prev) => {
      if (!(id in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const offset = Math.max(0, ...Object.values(barHeights));

  return (
    <ActionBarOffsetContext.Provider value={{ offset, register, unregister }}>
      {children}
    </ActionBarOffsetContext.Provider>
  );
};

/** Height (px) of the tallest bottom action bar currently on screen, or 0. */
export const useActionBarOffset = () =>
  React.useContext(ActionBarOffsetContext).offset;

/**
 * Registers a constant footprint (px) for a fixed element whose height is known
 * and shouldn't be measured — e.g. the floating SpeedDial FAB, whose root grows
 * when the dial opens, so we reserve just the resting button height instead.
 */
export const useRegisterActionBarHeight = (height: number, enabled = true) => {
  const { register, unregister } = React.useContext(ActionBarOffsetContext);
  const id = React.useId();

  React.useEffect(() => {
    if (!enabled) {
      unregister(id);
      return undefined;
    }
    register(id, height);
    return () => unregister(id);
  }, [id, enabled, height, register, unregister]);
};

/**
 * Registers a fixed bottom action bar so the app can reserve space for it (via
 * <ActionBarSpacer />) and lift overlays above it. Pass `enabled = false` when
 * the bar isn't acting as a bottom bar (e.g. a sidebar on desktop).
 */
export const useRegisterActionBar = (
  ref: React.RefObject<HTMLElement>,
  enabled = true,
) => {
  const { register, unregister } = React.useContext(ActionBarOffsetContext);
  const id = React.useId();

  React.useEffect(() => {
    const element = ref.current;

    if (!enabled || !element) {
      unregister(id);
      return undefined;
    }

    const update = () => register(id, element.offsetHeight);
    update();

    // ResizeObserver landed in Chrome 64, but the overlay that consumes this is
    // shown precisely for unsupported browsers (Chrome < 61). Fall back to
    // window resize events there so the offset still tracks bar height changes.
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      window.addEventListener("orientationchange", update);
      return () => {
        window.removeEventListener("resize", update);
        window.removeEventListener("orientationchange", update);
        unregister(id);
      };
    }

    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => {
      observer.disconnect();
      unregister(id);
    };
  }, [id, enabled, ref, register, unregister]);
};
